import { useUserProfile } from "@/contexts/ProfileContext";
import { Progress } from "@/components/ui/progress";
import { useDashboardAssessments } from "@/hooks/useDashboardAssessments";
import { Skeleton } from "@/components/ui/skeleton";

const MODULE_TITLES: Record<string, string> = {
  "owner-readiness": "mySuccession Snapshot",
  "owner-readiness-exit-timeline": "Readiness & Exit Timeline",
  "business-profile": "Business Profile",
  "business-valuation": "Business Valuation",
  "business-optimization": "Business Optimization Review",
  "advisory-team": "Advisory Team",
  "financial-readiness": "Financial Readiness",
  "successor-strategy": "mySuccession Strategy",
  "governance-family": "Governance & Family",
  "exit-structure": "Exit Structure & Tax Optimization",
  "mysuccession-plan": "mySuccession Plan Assembly",
};

function getTimeGreeting(): string {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return "Good morning";
  if (hour >= 12 && hour < 17) return "Good afternoon";
  return "Good evening";
}

function getCommentary(count: number, total: number): string {
  if (count === 0) return "Complete your first assessment to begin building your succession plan.";
  if (count >= total) return "All assessments complete — your succession plan is fully informed.";
  return "You're making progress — keep completing assessments to strengthen your plan.";
}

interface WelcomeBannerProps {
  onModuleSelect?: (moduleId: string, title: string) => void;
}

export function WelcomeBanner({ onModuleSelect }: WelcomeBannerProps) {
  const { displayName } = useUserProfile();
  const { completedCount, totalAssessments, isLoading } =
    useDashboardAssessments();

  if (isLoading) {
    return (
      <div className="pt-8 pb-6">
        <div className="flex items-center gap-6">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-4 flex-1" />
        </div>
      </div>
    );
  }

  const allComplete = completedCount >= totalAssessments;
  const progressPercent = (completedCount / totalAssessments) * 100;

  // Mode B — all complete
  if (allComplete) {
    return (
      <div className="pt-8 pb-6">
        <div className="space-y-1">
          <h2 className="text-3xl font-bold tracking-tight text-foreground">
            Welcome back, {displayName}
          </h2>
          <p className="text-sm text-muted-foreground">
            All assessments complete — your succession plan is fully informed.
          </p>
        </div>
      </div>
    );
  }

  // Mode A — in progress
  return (
    <div className="pt-8 pb-6">
      <div className="flex flex-col gap-6 md:flex-row md:items-center">
        {/* Left */}
        <div className="shrink-0 space-y-1">
          <h2 className="text-3xl font-bold tracking-tight text-foreground">
            {getTimeGreeting()}, {displayName}
          </h2>
          <p className="text-sm text-muted-foreground">
            {getCommentary(completedCount, totalAssessments)}
          </p>
        </div>

        {/* Right — progress */}
        <div className="ml-auto max-w-[520px] w-full space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Succession Plan Completeness</span>
            <span className="text-xs text-muted-foreground">{Math.round(progressPercent)}%</span>
          </div>
          <Progress value={progressPercent} className="h-2.5" />
        </div>

      </div>
    </div>
  );
}
